import { useState } from "react";
import { z } from "zod";
import { Link, useNavigate } from "react-router-dom";
import useMessage from "./customHook/message";

const SignUp = () => {
  const [signUpData, setSignUpData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const { contextHolder, success } = useMessage();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setSignUpData({ ...signUpData, [e.target.name]: e.target.value });
  };

  const validationSchema = z.object({
    name: z
      .string()
      .min(3, "Name must be at least 3 characters long")
      .max(30, "Name must be at most 30 characters long"),
    email: z.string().email("Invalid email address"),
    password: z.string().min(8, "Password must be at least 8 characters long"),
    confirmPassword: z
      .string()
      .min(8, "Confirm Password must be at least 8 characters long")
      .refine((val) => val === signUpData.password, {
        message: "Passwords do not match",
      }),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      validationSchema.parse(signUpData);
      setErrors({});

      await fetch("http://localhost:5000/userSignUp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(signUpData),
      });

      success("Signed up successfully");
      navigate("/Sign-In");
    } catch (err) {
      if (err.issues) {
        const errorMessages = err.issues.reduce((acc, curr) => {
          acc[curr.path[0]] = curr.message;
          return acc;
        }, {});
        setErrors(errorMessages);
      } else {
        console.error("Unexpected error structure", err);
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-12 text-slate-100 sm:px-6 lg:px-8">
      {contextHolder}
      <div className="mx-auto max-w-md rounded-[2rem] bg-slate-900/95 p-8 shadow-2xl shadow-slate-950/40 ring-1 ring-sky-400/20">
        <h2 className="text-center text-3xl font-bold text-white">Create your account</h2>
        <p className="mt-2 text-center text-sm text-slate-400">
          Join Tenzies and save your game history.
        </p>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-200">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={signUpData.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/50"
            />
            {errors.name && <p className="mt-2 text-sm text-rose-400">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-200">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={signUpData.email}
              onChange={handleChange}
              className="mt-1 block w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/50"
            />
            {errors.email && <p className="mt-2 text-sm text-rose-400">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-200">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={signUpData.password}
              onChange={handleChange}
              className="mt-1 block w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/50"
            />
            {errors.password && <p className="mt-2 text-sm text-rose-400">{errors.password}</p>}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-200">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={signUpData.confirmPassword}
              onChange={handleChange}
              className="mt-1 block w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/50"
            />
            {errors.confirmPassword && (
              <p className="mt-2 text-sm text-rose-400">{errors.confirmPassword}</p>
            )}
          </div>

          <button
            type="submit"
            className="w-full rounded-full bg-sky-500 py-3 text-base font-semibold text-slate-950 transition hover:bg-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-400/40"
          >
            Sign Up
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-400">
          Already have an account?{' '}
          <Link to="/Sign-In" className="font-medium text-sky-300 hover:text-sky-200">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SignUp;
